import { Guid } from "client_legacy/ndaw/guid";
import {
	ReactElement,
	useCallback,
	useMemo,
	useState
} from "react";
import {
	ActionListItem,
	ActionListSearch,
	ActionListSubMenuHeader,
	NoResults,
	SearchPlaceholder
} from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import { getClientId } from "client/Misc/clientId";
import { generateTrackingId } from "client/Misc/utils";
import { contains, normalizeForSearch } from "client/Data/searching";
import { GroupItemFormModel } from "./common";

export type GroupSelectionMenuProps = {
	containingGroupId?: Guid;
	moveToGroupTitle: string;
	groups: readonly GroupItemFormModel[];
	onBack: () => void;
	onMoveToGroup: (targetGroupId: Guid) => void;
};

export const GroupSelectionMenu = (props: GroupSelectionMenuProps): ReactElement => {
	const {
		containingGroupId,
		groups,
		moveToGroupTitle,
		onBack,
		onMoveToGroup
	} = props;

	const [searchTerm, setSearchTerm] = useState<string>("");

	const normalizedSearchTerm: string = useMemo(
		() => normalizeForSearch(searchTerm),
		[searchTerm]
	);

	const doesMatchSearchTerm = useCallback(
		(item: GroupItemFormModel): boolean => {
			if (normalizedSearchTerm === "") {
				return true;
			}

			const doesNameContainSearchTerm: boolean = contains(
				item.groupData.name,
				normalizedSearchTerm
			);

			return doesNameContainSearchTerm;
		},
		[normalizedSearchTerm]
	);

	const renderGroups = (): ReactElement => {
		if(searchTerm && !groups.some(doesMatchSearchTerm)) {
			return (
				<NoResults
					icon="magnifying-glass"
					title={localize("_NoSearchResults")}
					message={localize("_PleaseTrySearchingWithAnotherTerm")}
					trackingId="no-results"
				/>
			);
		}

		return (
			<>
				{groups.map(
					x => {
						if (!doesMatchSearchTerm(x)) {
							return null;
						}

						const clientId: Guid = getClientId(x);

						return (
							<GroupItem
								key={clientId}
								data={x}
								containingGroupId={containingGroupId}
								onClickGroup={onMoveToGroup}
							/>
						);
					}
				)}
			</>
		);
	};

	return (
		<>
			<ActionListSubMenuHeader
				text={moveToGroupTitle}
				onGoBack={onBack}
			/>
			<ActionListSearch
				onChange={setSearchTerm}
				icon="filter"
				placeholder={(
					<SearchPlaceholder
						kind="filter"
						properties={[localize("_Name")]}
					/>
				)}
			/>
			{renderGroups()}
		</>
	);
};
GroupSelectionMenu.displayName = "GroupSelectionMenu";

type GroupItemProps = {
	containingGroupId?: Guid;
	data: GroupItemFormModel;
	onClickGroup(clientId: Guid): void;
};

const GroupItem = (props: GroupItemProps): ReactElement | null => {
	const {
		containingGroupId,
		data,
		onClickGroup
	} = props;

	const clientId: Guid = getClientId(data);

	return (
		<ActionListItem
			key={clientId}
			onClick={() => onClickGroup(clientId)}
			text={data
				.groupData
				.name
			}
			trackingId={generateTrackingId("group", `|${clientId}`)}
			isDisabled={clientId === containingGroupId}
		/>
	);
};
GroupItem.displayName = "GroupItem";